
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  clearOAuthProvider,
  getPendingOAuthCallback
} from '../utils/oauth';

const AuthContext = createContext(null);

const ENV_BASE =
  (typeof import.meta !== 'undefined'
    ? import.meta.env?.VITE_FN_BASE
    : process.env?.REACT_APP_FN_BASE) || '';

const FN_BASE = String(ENV_BASE || '').replace(/\/$/, '');
const API_BASE = FN_BASE || '/auth';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SESSION_EXPIRED_MESSAGE = 'انتهت الجلسة، حدّث الصفحة ثم حاول مرة أخرى.';

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getCookie(name) {
  const m = document.cookie.match(
    new RegExp('(?:^|; )' + escapeRegex(name) + '=([^;]*)')
  );
  return m ? decodeURIComponent(m[1]) : '';
}

async function fx(path, opts = {}) {
  const method = (opts.method || 'GET').toUpperCase();
  const headers = { ...(opts.headers || {}) };

  let token = getCookie('XSRF-TOKEN');

  if (method !== 'GET') {
    if (token && !headers['X-XSRF-TOKEN']) headers['X-XSRF-TOKEN'] = token;
    if (!headers['Content-Type']) headers['Content-Type'] = 'application/json';
  }

  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

  let res = await fetch(url, { credentials: 'include', ...opts, headers });

  if (res.status === 403) {
    try {
      const clone = res.clone();
      const body = await clone.json().catch(() => ({}));

      if (body.error && (body.error.includes('csrf') || body.error === 'forbidden')) {
        await fetch(`${API_BASE}/csrf`, { credentials: 'include' });
        const newToken = getCookie('XSRF-TOKEN');
        if (newToken) {
            headers['X-XSRF-TOKEN'] = newToken;
            res = await fetch(url, { credentials: 'include', ...opts, headers });
        }
      }
    } catch {

    }
  }

  return res;
}

function normalizeEmail(value) {
  const e = String(value || '').trim().toLowerCase();
  return e.slice(0, 254);
}

function isValidEmail(email) {
  if (!email) return false;
  if (email.length > 254) return false;
  return EMAIL_REGEX.test(email);
}

function isStrongPassword(pw) {
  if (typeof pw !== 'string') return false;
  if (pw.length < 8) return false;
  if (pw.length > 128) return false;
  const hasLetter = /[A-Za-z]/.test(pw);
  const hasDigit = /\d/.test(pw);
  return hasLetter && hasDigit;
}

function mapRegisterError(code, j) {
  if (code === 'email_taken') return 'هذا البريد الإلكتروني مستخدم مسبقًا';
  if (code === 'invalid_email') return 'صيغة البريد الإلكتروني غير صحيحة';
  if (code === 'weak_password') return 'كلمة المرور يجب ألا تقل عن 8 أحرف وتحتوي على حروف وأرقام.';
  if (code === 'missing_fields') return 'البريد وكلمة المرور مطلوبة';
  if (code === 'bad_request') return 'بيانات غير صالحة، تحقق من الحقول';
  return j?.message || 'تعذر إنشاء الحساب';
}

function mapLoginError(code, j) {
  if (code === 'invalid_credentials') return 'تعذر تسجيل الدخول، تأكد من صحة البريد أو اسم المستخدم وكلمة المرور.';
  if (code === 'missing_fields') return 'الرجاء إدخال الإيميل أو اسم المستخدم وكلمة المرور';
  if (code === 'server_error') return 'حدث خطأ في الخادم، حاول لاحقًا.';
  return j?.message || 'فشل تسجيل الدخول';
}

function SessionExpiredModal({ open, onClose, onReload }) {
  if (!open) return null;

  const handleReload = () => {
    if (onReload) {
      onReload();
    } else {
      window.location.reload();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
      dir="rtl"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md"
        style={{
          background: 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.25) 0%, rgba(40, 227, 218, 0) 100%), #0b1c2b',
          borderRadius: '20px',
          border: '1px solid rgba(40, 227, 218, 0.25)',
          boxShadow: '0px 10px 40px rgba(0,0,0,.35)'
        }}
      >
        <div className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: 'rgba(255,255,255,.06)',
                border: '1px solid rgba(255,255,255,.12)'
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="1.6" opacity="0.8" />
                <path d="M12 7.5V13.5" stroke="white" strokeWidth="1.7" strokeLinecap="round" />
                <circle cx="12" cy="16.5" r="1" fill="white" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Alexandria' }}>
              انتهت الجلسة
            </h3>
          </div>

          <p className="text-white/90 mb-5 text-sm leading-relaxed" style={{ fontFamily: 'Alexandria' }}>
            انتهت صلاحية الجلسة الحالية لأسباب أمان. يرجى تحديث الصفحة ثم المحاولة مرة أخرى.
          </p>

          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 h-10 rounded-xl"
              style={{
                background: 'rgba(255,255,255,.06)',
                border: '1px solid rgba(255,255,255,.12)',
                color: '#fff',
                fontFamily: 'Alexandria'
              }}
            >
              إغلاق
            </button>
            <button
              onClick={handleReload}
              className="px-4 h-10 rounded-xl"
              style={{
                background: 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                border: 'none',
                color: '#fff',
                fontFamily: 'Alexandria'
              }}
            >
              تحديث الصفحة
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [booted, setBooted] = useState(false);
  const [error, setError] = useState(null);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);

  async function ensureCsrf() {
    try {
      if (!getCookie('XSRF-TOKEN')) {
         await fetch(`${API_BASE}/csrf`, { credentials: 'include' });
      }
    } catch {}
  }

  async function refreshMe({ silent = false } = {}) {
    try {
      const r = await fx('/me');

      if (r.status === 401) {
        setUser(null);
        if (!silent) {
          setError(SESSION_EXPIRED_MESSAGE);
          setSessionExpiredOpen(true);
        }
        return null;
      }

      const j = await r.json().catch(() => ({}));
      if (r.ok && j?.user) {
        setUser(j.user);
        return j.user;
      } else {
        setUser(null);
        return null;
      }
    } catch {
      setUser(null);
      return null;
    }
  }

  async function register({ email, password, username, displayName }) {
    setError(null);
    const e = normalizeEmail(email);
    const p = String(password || '');
    const u = String(username || '').trim();
    const dn = String(displayName || '').trim();

    if (!e || !p) {
      const msg = 'البريد وكلمة المرور مطلوبة';
      setError(msg);
      throw new Error(msg);
    }
    if (!isValidEmail(e)) {
      const msg = 'صيغة البريد الإلكتروني غير صحيحة';
      setError(msg);
      throw new Error(msg);
    }
    if (!isStrongPassword(p)) {
      const msg = 'كلمة المرور يجب ألا تقل عن 8 أحرف وتحتوي على حروف وأرقام.';
      setError(msg);
      throw new Error(msg);
    }
    if (!u) {
      const msg = 'اسم الحساب مطلوب';
      setError(msg);
      throw new Error(msg);
    }
    if (!/^[a-zA-Z0-9._-]{5,30}$/.test(u)) {
      const msg = 'اسم الحساب 5–30 حروف/أرقام ويمكن أن يحتوي . _ - فقط';
      setError(msg);
      throw new Error(msg);
    }

    await ensureCsrf();

    const r = await fx('/register', {
      method: 'POST',
      body: JSON.stringify({ email: e, password: p, username: u, displayName: dn })
    });

    const j = await r.json().catch(() => ({}));

    if (!r.ok) {
      const code = j?.error || '';
      const msg = mapRegisterError(code, j);
      setError(msg);
      throw new Error(msg);
    }

    if (j?.user) setUser(j.user);
    else await refreshMe({ silent: true });

    setError(null);
    return true;
  }

  async function login(identifier, password) {
    setError(null);
    const id = String(identifier || '').trim();
    const p = String(password || '');

    if (!id || !p) {
      const msg = 'الرجاء إدخال الإيميل أو اسم المستخدم وكلمة المرور';
      setError(msg);
      throw new Error(msg);
    }

    await ensureCsrf();

    let r, j;
    try {
      r = await fx('/login', {
        method: 'POST',
        body: JSON.stringify({ identifier: id, password: p })
      });
    } catch (err) {
      setUser(null);
      setError('تعذر الاتصال بالخادم');
      throw err;
    }

    try { j = await r.json(); } catch { j = {}; }

    if (!r.ok) {
      const code = j?.error || '';
      const msg = mapLoginError(code, j);
      setUser(null);
      setError(msg);
      throw new Error(msg);
    }

    setUser(j.user || null);
    setError(null);
    return true;
  }

  async function logout() {
    setError(null);
    try {
      await ensureCsrf();
      await fx('/logout', { method: 'POST' });
    } catch {}
    setUser(null);
  }

async function updateProfile({ name, specialization, batch }) {
  setError(null);
  await ensureCsrf();

  const r = await fx('/user/profile', {
    method: 'PUT',
    body: JSON.stringify({ name, specialization, batch })
  });

  if (r.status === 401 || r.status === 403) {
    const j = await r.json().catch(() => ({}));
    if (j.error !== 'csrf_failed') {
      setUser(null);
      setError(SESSION_EXPIRED_MESSAGE);
      setSessionExpiredOpen(true);
      throw new Error(SESSION_EXPIRED_MESSAGE);
    }
  }

  const j = await r.json().catch(() => ({}));

  if (!r.ok) {
    const msg = j?.message || 'تعذر حفظ البيانات';
    setError(msg);
    throw new Error(msg);
  }

  if (j?.user) setUser(j.user);
  return true;
}


  function clearError() {
    setError(null);
  }

  useEffect(() => {
    if (!error) return;
    if (error.includes('انتهت الجلسة')) {
      setSessionExpiredOpen(true);
    }
  }, [error]);

  useEffect(() => {
    const pendingOAuthCallback = getPendingOAuthCallback(API_BASE);
    (async () => {
      if (pendingOAuthCallback) {
        let currentUser = await refreshMe({ silent: true });

        if (!currentUser) {
          try {
            await fetch(pendingOAuthCallback, {
              credentials: 'include',
              cache: 'no-store',
              redirect: 'follow'
            });
          } catch {}

          currentUser = await refreshMe({ silent: true });
        }

        clearOAuthProvider();
        window.history.replaceState(
          {},
          document.title,
          `${window.location.pathname}${window.location.hash}`
        );
        setBooted(true);
        return;
      }

      clearOAuthProvider();
      await ensureCsrf();
      await refreshMe({ silent: true });
      setBooted(true);
    })();
  }, []);

  const role = useMemo(() => (user?.role || '').toLowerCase(), [user]);

  const handleSessionModalClose = () => {
    setSessionExpiredOpen(false);
    if (error && error.includes('انتهت الجلسة')) {
      setError(null);
    }
  };

  const handleSessionReload = () => {
    window.location.reload();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        role,
        register,
        login,
        logout,
        refreshMe,
        updateProfile,
        booted,
        error,
        clearError
      }}
    >
      {children}
      <SessionExpiredModal
        open={sessionExpiredOpen}
        onClose={handleSessionModalClose}
        onReload={handleSessionReload}
      />
    </AuthContext.Provider>
  );
};
